<template>
  <div>
    <main-layout
      controlsBg="light"
      :hasControlsBar="false"
      :breadcrumb="getBreadcrumb">
      <div class="container">
        <grid-row  class="margin-top-xl" column-align="center">
          <grid-col col-size="4">
            <div id="header">
              <h1 class="header-title">
                <chevron
                  @click="$router.push({name: 'AggregateVariableMenu'})"/>
                {{ $t('aggregateVariable.gradualVariables') }}
              </h1>
              <div class="header-add-aggregate-gradual-variable">
                <router-link to="/aggregate-variable/gradual">
                <span
                  v-tooltip.left.top-start="$t('aggregateGradualVariable.addAggregateGradualVariable')">
                  <font-awesome-icon class="header-add-aggregate-gradual-variable-icon opacity"
                                     :icon="icon.add"/>
                </span>
                </router-link>
              </div>
            </div>
            <alert
              v-if="alertVisible"
              :message="alertMessage"
              :type="alertType">
            </alert>
          </grid-col>
        </grid-row>
        <grid-row  class="margin-top-xl" column-align="center">
          <grid-col col-size="4">
            <table-component
              ref="aggregateGradualVariableTable"
              :data="fetchData"
              sort-by="name"
              sort-order="asc"
              :show-caption="false"
              show-filter=false
              class="datatable"
              filter-placeholder=""
              filterNoResults="">
              <!--
              <table-column
                :sortable="false"
                :filterable="false"
                header-class="table-component__minimum__width__column"
                cell-class="text-center">
                <template slot-scope="row">
                  <span
                    v-tooltip.right.top-start="{
                      content: displayFormula(row)
                    }">
                  <font-awesome-icon
                    :id="row"
                    :icon="icon.formula"/>
                  </span>
                </template>
              </table-column>
              -->
              <table-column
                show="name"
                header-class="text-left table-component__wider__width__column"
                cell-class="table-component__text-left"
                :label="$t('aggregateVariable.name')">
              </table-column>
              <table-column
                show="unit"
                header-class="text-left"
                cell-class="table-component__text-left"
                :label="$t('aggregateVariable.unit')">
              </table-column>
              <table-column
                show="createdAt"
                cell-class="table-component__text-center"
                data-type="date:LL"
                :label="$t('aggregateVariable.creationDate')"
                :formatter="dateFormatter">
              </table-column>
              <table-column
                show="owner"
                header-class="text-left"
                cell-class="table-component__text-left"
                :label="$t('aggregateVariable.createdBy')">
              </table-column>
              <table-column
                label=""
                :sortable="false"
                :filterable="false"
                header-class="table-component__minimum__width__column"
                cell-class="text-center">
                <template slot-scope="row">
                  <font-awesome-icon
                    v-if="row.canShare"
                    :id="row"
                    v-on:click="onShare(row)"
                    :icon="icon.share"
                    class="table-iconset-icon"/>
                  <font-awesome-icon
                    v-else
                    :icon="icon.share"
                    class="table-icon-disabled"/>
                </template>
              </table-column>
              <table-column
                label=""
                :sortable="false"
                :filterable="false"
                header-class="table-component__minimum__width__column"
                cell-class="text-center">
                <template slot-scope="row">
                  <font-awesome-icon
                    v-if="row.canEdit"
                    :id="row"
                    v-on:click="onUpdate(row)"
                    :icon="icon.pencil"
                    class="table-iconset-icon"/>
                  <font-awesome-icon
                    v-else
                    :icon="icon.pencil"
                    class="table-icon-disabled"/>
                </template>
              </table-column>
              <table-column
                label=""
                :sortable="false"
                :filterable="false"
                header-class="table-component__minimum__width__column"
                cell-class="text-center">
                <template slot-scope="row">
                  <font-awesome-icon
                    v-if="row.canDelete"
                    :id="row"
                    v-on:click="onDelete(row)"
                    :icon="icon.trash"
                    class="table-iconset-icon"/>
                  <font-awesome-icon
                    v-else
                    :icon="icon.trash"
                    class="table-icon-disabled"/>
                </template>
              </table-column>
            </table-component>
          </grid-col>
        </grid-row>
      </div>
      <ok-cancel-modal
        v-if="showConfirmationDeleteModal"
        v-on:ok="onDeleteConfirm"
        v-on:cancel="onDeleteCancel"
        :title="deleteModalMessage"
        :message="deleteModalMessage"
        :okButton="$t('commons.delete')"
        :cancelButton="$t('commons.cancel')">
        <div slot="body">
          <ul>
            <li>
              {{ variableToDelete.name }}
            </li>
          </ul>
        </div>
      </ok-cancel-modal>
      <accept-modal
        v-if="showDeleteDependenciesModal"
        v-on:accept="showDeleteDependenciesModal = false"
        :title="$t('aggregateVariable.deleteDependenciesMessage')"
        :acceptButton="$t('commons.accept')">
        <div slot="body">
          <h4 v-if="deleteDependendentVariables.length > 0">{{ $t('aggregateVariable.variableDependencies') }}:</h4>
          <ul>
            <li v-for="variable in deleteDependendentVariables"
                :key="variable.id">
              {{ variable }}
            </li>
          </ul>
          <h4 v-if="deleteDependendentRules.length > 0">{{ $t('aggregateVariable.ruleDependencies') }}:</h4>
          <ul>
            <li v-for="variable in deleteDependendentRules"
                :key="variable.id">
              {{ variable }}
            </li>
          </ul>
        </div>
      </accept-modal>
    </main-layout>
  </div>
</template>

<script>
import MainLayout from '@/layouts/MainLayout';
import GridRow from '@/components/shared/grid/GridRow';
import GridCol from '@/components/shared/grid/GridCol';
import {mapActions, mapGetters} from 'vuex';
import FontAwesomeIcon from '@fortawesome/vue-fontawesome';
import pagination from '@/components/shared/table/pagination';
import { faPencilAlt, faTrashAlt, faKey, faFunction } from '@fortawesome/pro-regular-svg-icons';
import {faPlusCircle} from '@fortawesome/pro-solid-svg-icons';
import TableComponent from '@/components/shared/table/components/TableComponent';
import TableColumn from '@/components/shared/table/components/TableColumn';
import Alert from '@/components/shared/alert/Alert';
import OkCancelModal from '@/components/shared/dialogs/OkCancelModal';
import AcceptModal from '../../components/shared/dialogs/AcceptModal';
import BtnPill from '@/components/shared/buttons/BtnPill';
import dayjs from 'dayjs';
import Chevron from '@/components/shared/Chevron';
import dependencies from '@/layouts/variables/dependencies';

export default {
  components: {
    MainLayout,
    GridRow,
    GridCol,
    TableComponent,
    TableColumn,
    FontAwesomeIcon,
    OkCancelModal,
    AcceptModal,
    Alert,
    BtnPill,
    Chevron
  },
  data () {
    return {
      titleKey: '',
      variableToDelete: -1,
      showConfirmationDeleteModal: false,
      showDeleteDependenciesModal: false,
      deleteDependendentVariables: [],
      deleteDependendentRules: [],
      alertVisible: false,
      alertMessage: '',
      alertType: '', // error, warning, success
      deleteModalMessage: '',
      tablePageSize: 10,
      tableState: {
        filter: '',
        page: 1,
        sort: {
          order: '',
          fieldName: ''
        }
      },
      icon: {
        add: faPlusCircle,
        pencil: faPencilAlt,
        trash: faTrashAlt,
        share: faKey,
        formula: faFunction
      },
      breadcrumb: [
        {link: '/workspace', nameKey: 'menu.workspace'},
        {link: '/aggregate-variable/menu', nameKey: 'aggregateVariable.title'},
        {link: '/aggregate-variable/gradual/list', nameKey: 'aggregateVariable.gradualVariables', disabled: true}
      ]
    };
  },
  watch: {
    getAggregateGradualVariables: {
      handler: function () {
        this.$refs.aggregateGradualVariableTable.refresh();
      }
    }
  },
  created () {
    // this.retrieveVariables({ context: this });
    this.retrieveAggregateVariables({ context: this });
    this.retrieveRules({ context: this });

    const alert = this.$route.params.alert;
    if (alert !== undefined) {
      showAlert(this, this.$t(alert), 'success');
    }
  },
  computed: {
    ...mapGetters([
      'getVariables',
      'getAggregateVariables',
      'getRules',
      'getAggregateGradualVariables',
      'getLanguageCode'
    ]),
    getBreadcrumb () {
      return this.breadcrumb.map(route => {
        route.name = this.$t(route.nameKey);
        return route;
      });
    }
  },
  methods: {
    ...mapActions([
      'retrieveVariables',
      'retrieveAggregateVariables',
      'retrieveRules',
      'deleteAggregateVariable'
    ]),
    onUpdate (aggregateVariable) {
      this.$router.push({ name: 'AggregateGradualVariable', params: aggregateVariable });
    },
    onDelete (aggregateGradualVariable) {
      const dependentVariables = this.getDependentVariables(aggregateGradualVariable);
      const dependentRules = this.getDependentRules(aggregateGradualVariable);
      if (dependentVariables.length > 0 || dependentRules.length > 0) {
        this.showDeleteDependenciesModal = true;
        this.deleteDependendentVariables = dependentVariables;
        this.deleteDependendentRules = dependentRules;
      } else {
        this.variableToDelete = aggregateGradualVariable;
        this.deleteModalMessage = this.$t('aggregateVariable.deleteModalMessage');
        this.showConfirmationDeleteModal = true;
      }
    },
    getDependentVariables (variable) {
      return dependencies.getDependencies(variable, this.getAggregateVariables);
    },
    getDependentRules (variable) {
      return dependencies.getDependencies(variable, this.getRules);
    },
    onDeleteConfirm () {
      const id = this.variableToDelete.id;
      const varName = this.variableToDelete.name;
      this.variableToDelete = {};
      this.showConfirmationDeleteModal = false;
      this.deleteAggregateVariable({ context: this, id: id })
        .then(() => {
          showAlert(this, this.$t('aggregateVariable.successDelete', {name: varName}), 'success');
        }, () => {
          showAlert(this, this.$t('aggregateVariable.errorDelete', {name: varName}), 'error');
        });
    },
    onDeleteCancel () {
      this.variableToDelete = {};
      this.showConfirmationDeleteModal = false;
    },
    onShare (aggregateGradualVariable) {
      this.$router.push({
        name: 'Permissions',
        params: {
          titleKey: 'permission.titleAggregateVariable',
          type: 'variable',
          id: aggregateGradualVariable.id,
          breadcrumb: this.breadcrumb
        }});
    },
    displayFormula (variable) {
      const aggregateVariable = getVarById(this.getVariables, variable.id);
      if (aggregateVariable) {
        const variableLabel = aggregateVariable.label.split('_').join(' ');
        const timeGap = this.gettimeGapSelectOptions().find(option => option.value === variable.timeGap);
        return this.$t('aggregateVariable.gradualIncrement', {name: variableLabel, timeGap: timeGap.label});
      }

      return '';
    },
    gettimeGapSelectOptions () {
      return [
        {
          label: this.$t('aggregateGradualVariable.oneMinute'),
          value: 60
        },
        {
          label: this.$t('aggregateGradualVariable.oneHour'),
          value: 3600
        },
        {
          label: this.$t('aggregateGradualVariable.oneDay'),
          value: 86400
        },
        {
          label: this.$t('aggregateGradualVariable.oneMonth'),
          value: 2592000
        }
      ];
    },
    dateFormatter (value) {
      const languageCode = this.getLanguageCode;
      return dayjs(value).locale(languageCode).format('D MMMM YYYY');
    },
    fetchData ({ page, filter, sort }) {
      const tablePage = pagination.createPage(
        this.getAggregateGradualVariables,
        this.tableState,
        ['name', 'unit', 'owner'],
        this.tablePageSize,
        { page, filter, sort });
      this.tableState = tablePage.state;
      return tablePage.result;
    }
  }
};

const showAlert = (context, message, type) => {
  context.alertVisible = true;
  context.alertMessage = message;
  context.alertType = type;
  context.showAlert = true;
  setTimeout(() => {
    context.alertVisible = false;
  }, 2000);
};

const getVarById = (list, ref) => {
  const id = Number(ref);
  for (let i = 0; i < list.length; i++) {
    if (id === list[i].value.id) {
      return list[i];
    }
  }
};

</script>

<style lang="scss" >
  @import "src/stylesheets/variables";

  #header {
    display: inline-block;
    width: 100%;
    line-height: 1rem;
  }

  .header-title {
    float: left;
  }

  .header-add-aggregate-gradual-variable {
    float: right;

  }

  .header-add-aggregate-gradual-variable-icon {
    font-size: 3rem;
    color: $secondary;
  }

  .table-iconset-icon {
    cursor: pointer;
  }

  .opacity:hover {
    opacity: 0.3;
    transition: opacity 0.3s;
  }
</style>



// WEBPACK FOOTER //
// src/layouts/variables/AggregateGradualVariableList.vue